import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {makeQueryBuilder, makeQueryRules} from '@src/app/ui/ui.module';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {first, map, tap} from 'rxjs/operators';

import {basePath} from '../injection';
import {
  BaseSettings,
  ResponseSuccess,
  Settings,
  SettingsFilter,
  SettingsFilterMeta,
  SettingsState,
  SystemSettings,
  UserSettings,
} from '../models';
import {generateIndexQueryString} from '../utils';

const initialSettingsState: SettingsState = {
  system: undefined,
  user: undefined,
};

export type CollectionOrderSettings = Record<string, string[]>;//map of collections ids containing sorted objects ids

const INFO_KEY = 'atmi-settings';

@Injectable({providedIn: 'root'})
export class SettingsApiService {
  private readonly settingsPath = `${this.baseUrl}/settings`;

  private readonly state$ = new BehaviorSubject<SettingsState>(initialSettingsState);
  collectionOrderSettings: CollectionOrderSettings = {};
  collectionOrderSettings$: BehaviorSubject<CollectionOrderSettings> = new BehaviorSubject<CollectionOrderSettings>({});
  collectionOrderSettingsId: string = '';
  collectionOrderSettingsObject: Settings<any> = {
    id: '', name: 'collectionOrderSettings',
    scope: 'user',
    value: {},
  };

  constructor(
    @Inject(basePath) private readonly baseUrl: string,
    private readonly http: HttpClient,
  ) {
    const stateFromStorage = localStorage.getItem(INFO_KEY);

    if (stateFromStorage) {
      const state: SettingsState = JSON.parse(stateFromStorage);
      this.setState(state);
    }

    /*on boot verify whether collection sort settings exists server side otherwise create it as an empty map {}*/
    // Delay setup to prevent errors during login page initialization
    setTimeout(() => {
      this.setupCollectionOrderSettings();
    }, 2000);
  }

  /*@TODO filter by current user???*/
  setupCollectionOrderSettings() {
    // Guard: do nothing if not authenticated; prevents 400/401 during bootstrap
    const userStateRaw = localStorage.getItem('atmi-user');
    if (!userStateRaw) {
      return;
    }
    try {
      const userState = JSON.parse(userStateRaw);
      if (!userState?.token) {
        return;
      }
    } catch {
      return;
    }

    this.settings<any>().pipe(
      first(),
      map(async (allSettings) => {
        const collectionOrderSettingsEntry: Settings<Array<CollectionOrderSettings>> | undefined = allSettings
          ?.find((userSetting) => userSetting.name === 'collectionOrderSettings' && userSetting.scope === 'user');

        if (collectionOrderSettingsEntry) {
          this.collectionOrderSettingsId = collectionOrderSettingsEntry.id;
          if (!collectionOrderSettingsEntry.value) {
            collectionOrderSettingsEntry.value = [{}];
          }
          this.collectionOrderSettingsObject = collectionOrderSettingsEntry;
          this.collectionOrderSettings =
            (collectionOrderSettingsEntry.value?.[0] as CollectionOrderSettings) || {};
          this.collectionOrderSettings$.next(this.collectionOrderSettings);
        } else {
          try {
            const answ = await this.createSettings({
              name: 'collectionOrderSettings',
              scope: 'user',
              value: [{}],
            });
            this.collectionOrderSettingsId = answ.data.id;
            this.collectionOrderSettings =
              (answ.data.value?.[0] as CollectionOrderSettings) || {};
            this.collectionOrderSettings$.next(this.collectionOrderSettings);
          } catch (error) {
            console.warn('Failed to create collection order settings:', error);
            this.collectionOrderSettings = {};
            this.collectionOrderSettings$.next({});
          }
        }
      }),
    ).subscribe({
      error: (error) => {
        console.warn('Settings setup failed:', error);
        this.collectionOrderSettings = {};
        this.collectionOrderSettings$.next({});
      },
    });
  }

  async createSettings(settings: Omit<Settings<any>, 'id'>) {
    return firstValueFrom(this.http.post<{
      statusCode: number,
      data: Settings<any>,
    }>(this.settingsPath, settings).pipe(first()));
  }

  async putSettings<T>(settings: Settings<T>) {
    return firstValueFrom(this.http.put(`${this.settingsPath}/${settings.id}`, settings).pipe(first()));
  }

  async updateCollectionOrderSettings(_settings: CollectionOrderSettings) {

    const settings = {
      id: this.collectionOrderSettingsId,
      value: _settings,
      name: 'collectionOrderSettings',
      scope: 'user'
    } as Settings<Record<string, string[]>>;

    const value = [{...this.collectionOrderSettingsObject.value[0], ..._settings}]
    const updateRes = this.putSettings<CollectionOrderSettings>({
      ...this.collectionOrderSettingsObject,
      value
    });

    this.collectionOrderSettings = value[0] as CollectionOrderSettings;
    console.log({settings}, 'settings just saved')
    this.collectionOrderSettings$.next(this.collectionOrderSettings);
  }


  settings<T>(filters?: SettingsFilter) {
    const rules = makeQueryRules(filters, SettingsFilterMeta);
    const filter = makeQueryBuilder(rules);
    const params = generateIndexQueryString({filter, page: 0, limit: -1});

    return this.http.get<ResponseSuccess<Settings<T>[]>>(this.settingsPath, {params}).pipe(
      map((res) => res.data),
      first(),
    );
  }

  systemSettings() {
    return this.settings<SystemSettings>({scope: 'system'}).pipe(
      tap((settings) => {
        if (!settings || settings.length === 0) {
          console.warn('systemSettings: received empty settings array, applying defaults');
          this.setState({system: undefined});
          return;
        }

        const first = settings[0];
        if (!first || first.value == null) {
          console.warn('systemSettings: first setting has no value, applying defaults');
          this.setState({system: undefined});
          return;
        }

        const sysSettingsArray = Array.isArray(first.value)
          ? (first.value as SystemSettings[])
          : [first.value as SystemSettings];

        if (!sysSettingsArray.length) {
          console.warn('systemSettings: value array is empty, applying defaults');
          this.setState({system: undefined});
          return;
        }

        const sysSettings = sysSettingsArray[0];
        this.setState({system: sysSettings});
      }),
    );
  }

  userSettings(_: string) {
    return this.settings<UserSettings[]>({scope: 'user'});
  }

  selectState() {
    return this.state$.asObservable();
  }

  selectStateSnapshot() {
    return this.state$.value;
  }

  private setState(state: Partial<SettingsState>) {
    const newState: SettingsState = {...this.state$.value, ...state};

    this.state$.next(newState);
    localStorage.setItem(INFO_KEY, JSON.stringify(newState));
  }
}
